import {useMemo, useState} from "react";
import {EnsAlertSummary, EnsLevelDto, EnsNotification} from "../../../store/features/ens/types";
import {useFetchUsersQuery} from "../../../store/features/Users/userService";
import {formatDateTime} from "../utils";
import {getDispatchEventLabel} from "./timelineDisplayUtils";

interface Props {
    summary: EnsAlertSummary;
    levels: EnsLevelDto[];
    notifications: EnsNotification[];
}

type EventType = 'created' | 'scheduled' | 'dispatched' | 'delivered' | 'confirmed' | 'failed' | 'cancelled' | 'timeout';
type FilterKey = 'ALL' | number;

interface TimelineEvent {
    time: string;
    label: string;
    type: EventType;
}

interface LevelTimelineGroup {
    level: EnsLevelDto;
    notifications: EnsNotification[];
    events: TimelineEvent[];
    confirmedCount: number;
    failedCount: number;
    totalCount: number;
}

const DOT_COLOR: Record<EventType, string> = {
    created: 'bg-brand',
    scheduled: 'bg-ink-disabled',
    dispatched: 'bg-blue-400',
    delivered: 'bg-blue-500',
    confirmed: 'bg-green-500',
    failed: 'bg-red-500',
    cancelled: 'bg-ink-disabled',
    timeout: 'bg-orange-400',
};

const friendlyFailureReason = (error: string | null): string => {
    if (!error) return 'Notification could not be delivered.';
    const normalized = error.toLowerCase();
    if (
        normalized.includes('missing eligible external ens msisdn')
        || normalized.includes('missing eligible external contact phone number')
        || normalized.includes('missing destination msisdn')
    ) {
        return 'Missing eligible external contact phone number.';
    }
    if (
        normalized.includes('no active push device')
        || normalized.includes('not sent to any registered device')
        || normalized.includes('no active mobile device for push notifications')
    ) {
        return 'Recipient has no active mobile device for push notifications.';
    }
    if (normalized.includes('no active mobile device for in-app calls')) {
        return 'Recipient has no active mobile device for in-app calls.';
    }
    if (normalized.includes('provider is not configured') || normalized.includes('dispatcher') || normalized.includes('gateway config')) {
        return 'This notification channel is not available yet.';
    }
    if (normalized.includes('missing voice prompt') || normalized.includes('voice prompt s3 url')) {
        return 'Voice prompt audio is temporarily unavailable.';
    }
    if (normalized.includes('missing caller msisdn')) {
        return 'Phone call provider is not configured.';
    }
    if (normalized.includes('rate limiter')) {
        return 'Notification could not be started because the system is busy.';
    }
    return 'Notification could not be delivered.';
};

export const TimelineTab = ({summary, levels, notifications}: Props) => {
    const {data: users = []} = useFetchUsersQuery({});
    const [activeLevel] = useState<FilterKey>('ALL');

    const userNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        users.forEach((u) => {
            const name = `${u.firstName} ${u.lastName}`.trim();
            map[u.id] = name;
        });
        return map;
    }, [users]);

    const groups = useMemo<LevelTimelineGroup[]>(() => {
        const resolve = (userId: number) => userNameMap[userId] ?? `User #${userId}`;
        const notificationsByLevel = new Map<number, EnsNotification[]>();
        notifications.forEach((notification) => {
            const items = notificationsByLevel.get(notification.levelId) ?? [];
            items.push(notification);
            notificationsByLevel.set(notification.levelId, items);
        });

        return levels.map((level) => {
            const levelNotifications = notificationsByLevel.get(level.id) ?? [];
            const events = levelNotifications.flatMap<TimelineEvent>((notification) => {
                const user = resolve(notification.recipientUserId);
                const notificationEvents: TimelineEvent[] = [];
                notificationEvents.push({
                    time: notification.scheduledAt,
                    label: `${user}: scheduled for L${level.sequenceNo}`,
                    type: 'scheduled',
                });
                if (notification.dispatchRequestedAt) {
                    notificationEvents.push({
                        time: notification.dispatchRequestedAt,
                        label: `${user}: queued for delivery`,
                        type: 'dispatched',
                    });
                }
                if (notification.dispatchedAt) {
                    notificationEvents.push({
                        time: notification.dispatchedAt,
                        label: getDispatchEventLabel(user, notification),
                        type: 'dispatched',
                    });
                }
                if (notification.deliveredAt) {
                    notificationEvents.push({
                        time: notification.deliveredAt,
                        label: `${user}: delivered`,
                        type: 'delivered',
                    });
                }
                if (notification.confirmedAt) {
                    notificationEvents.push({
                        time: notification.confirmedAt,
                        label: `${user}: confirmed`,
                        type: 'confirmed',
                    });
                }
                if (notification.failedAt) {
                    notificationEvents.push({
                        time: notification.failedAt,
                        label: `${user}: ${friendlyFailureReason(notification.lastError)}`,
                        type: 'failed',
                    });
                }
                if (notification.status === 'CANCELLED') {
                    notificationEvents.push({
                        time: notification.updatedAt,
                        label: `${user}: stopped because no further escalation is needed`,
                        type: 'cancelled',
                    });
                }
                if (notification.status === 'TIMEOUT') {
                    notificationEvents.push({
                        time: notification.updatedAt,
                        label: `${user}: no confirmation received before the next escalation`,
                        type: 'timeout',
                    });
                }
                return notificationEvents;
            }).sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

            return {
                level,
                notifications: levelNotifications,
                events,
                confirmedCount: levelNotifications.filter((n) => n.status === 'CONFIRMED').length,
                failedCount: levelNotifications.filter((n) => n.status === 'FAILED').length,
                totalCount: levelNotifications.length,
            };
        });
    }, [levels, notifications, userNameMap]);

    const visibleGroups = activeLevel === 'ALL'
        ? groups
        : groups.filter((group) => group.level.sequenceNo === activeLevel);

    const timelineEvents: TimelineEvent[] = [
        {time: summary.triggeredAt, label: 'Alert created', type: 'created' as const},
        ...visibleGroups.flatMap((group) => group.events),
    ].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    return (
        <div className="flex flex-col gap-3 px-4 py-3">
            <div className="flex flex-col">
                {timelineEvents.map((event, index) => {
                    const isLast = index === timelineEvents.length - 1;
                    return (
                        <div key={`${event.time}-${index}`} className="flex gap-2.5">
                            <div className="flex flex-col items-center">
                                <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${DOT_COLOR[event.type]}`}/>
                                {!isLast && <span className="mt-1 w-px flex-1 bg-surface-muted"/>}
                            </div>
                            <div className={`min-w-0 ${isLast ? '' : 'pb-4'}`}>
                                <div className="text-xs font-medium leading-snug text-ink">{event.label}</div>
                                <div className="mt-0.5 text-[11px] text-ink-muted">{formatDateTime(event.time)}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
